/* global Office, console */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import HeaderPanel from "../../layout/HeaderPanel";
import { IButtonProps, IContextualMenuItem, ImageFit, mergeStyleSets, nullRender } from "@fluentui/react";
import FooterPanel from "../../layout/FooterPanel";
import { IconButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { DocumentPartTreeSelection, getWorkflowStatus, TreeViewDataItem } from "../DocumentPartTree";
import {
  fetchCheckUnlinkPart,
  fetchDocumentMetadata,
  fetchDocumentPartDetails,
  fetchDriveItemName,
  fetchParentsDocumentPart,
  fetchRecursiveParentsDocumentPart,
  fetchRootsDocumentPart,
} from "../../../api/document/DocumentsPart";
import { CreateDocumentPartModal } from "../CreateDocumentPartModal";
import { openDocumentById } from "../../../utils/helpers/OpenDocumentHelper";
import {
  DocumentPartItem,
  editDocumentMetadataAsync,
  unlinkDocumentPartAsync,
} from "../../../redux/feature/document/document-slice";
import { ElaborationWrapper } from "../../coobjects/ElaborationWrapper";
import {
  INSERT_OBJECT_NOTIFICATION,
  REFRESH_DOCUMENT_PROPERTIES_NOTIFICATION,
} from "../../../utils/constants/notification";
import LoadingManager from "../../../common/loading/LoadingManager";
import {
  formatMessage,
  formatMessageWithParams,
  formatSehorseLastUpdateDate,
} from "../../../utils/helpers/LanguageHelper";
import { DocumentData, EditDocument } from "../../document/EditDocument";
import {
  editDocumentAdvancedCustomXML,
  mapAdvancedDocumentToDocPartMetadata,
} from "../../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import { ExportDocumentPanel } from "../../document/ExportDocumentPanel";
import { NumberFormat } from "../../../api/models/number-format";
import { DuplicateDocumentPanel } from "../../document/DuplicateDocumentPanel";
import NotificationManager from "../../../common/notification/NotificationManager";
import { DocumentPart } from "../../../api/models";
import { DetachDocumentPanel } from "../../document/DetachDocumentPanel";
import { FindItem, useFilteredElaborations } from "../../generic/GenericElaborationSelector";
import { ButtonVariants } from "../../../styles/fluent/ButtonVariants";
import { FlatPathTreeItem } from "../../generic/FlatPathTreeExplorer";
import { DocumentOutline } from "../../document/DocumentOutline";
import { UserFunction } from "../../../api/models/user-function";
import { useAppSelector } from "../../../redux/hooks/hooks";
import {
  selectIxbrlSettings,
  selectSeahorseDocumentLinked,
  selectServiceProvider,
  selectUserFunctions,
  setSeahorseDocumentLinked,
} from "../../../redux/feature/settings/configurations-slice";
import { isNotAuthorizedUser } from "../../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../../api/models/user-function-enum";
import { DocumentPosition } from "../../../api/models/document-position";
import { DeleteDocumentHelper } from "../../../utils/helpers/DeleteDocumentsHelper";
import { DocumentPropertiesActionTypes } from "../DocumentPropertiesMessagesModal";
import { ServiceProvider } from "../../../api/models/service-provider";
import {
  hasNoIxbrlSetting,
  fetchSeahorseDocumentLinked,
  hasNoDefaultServiceProvider,
  sendDocumentToIXBRL,
} from "../../../pages/seahorse/SeahorseUtils";
import { checkIfPartExists } from "../../../office/WordDocumentPart";
import { SeahorseDocumentLinked } from "../../../api/models/seahorse-document-linked";
import { store } from "../../../redux/store/store";
import { exportObjects } from "../../../api/object/CoObjectGeneral";
import { saveAs } from "file-saver";
import { useCloneDocPart } from "./useCloneDocPart";
import { DocumentPartWithStatus } from "../../../api/models/document-part-with-status";
import { elaborationStatusItemIsBlocking } from "../../../redux/feature/object/remote-elaborations-slice";
import { IxbrlSetting } from "../../../api/models/ixbrl-setting";
import { TreeItemRow } from "../TreeItemRow";

const loadingManagerKey = "DOCUMENT_PROPERTIES";
const SUBMITTED: string = "50";

export async function mapDocPartToDataItem(documentPart: DocumentPart): Promise<TreeViewDataItem> {
  return {
    key: documentPart.id,
    driveItemId: documentPart.driveItemId,
    text: documentPart.name,
    order: documentPart.order,
    nodeType: documentPart.root ? "root" : "part",
    hasChildren: documentPart.hasChildren,
    expanded: documentPart.subparts?.length > 0,
    checked: false,
    selected: false,
    data: documentPart,
  };
}

type DocumentPropertiesPages = "outline" | "adddocpart" | "export" | "duplicate" | "edit" | "detach";

interface DocumentPropertiesState {
  key: number;
  partRoot: DocumentPart;
  initialDocumentData: {
    initialDocumentItem: FlatPathTreeItem;
    parents: DocumentPosition[];
  };
  itemSelected: DocumentPartTreeSelection | null;
  showMessagesModal: boolean;
  linkedPart: DocumentPartItem;
  selectedPart: DocumentPartItem;
  deletingPart: DocumentPartItem;
  action: DocumentPropertiesActionTypes;
  page: DocumentPropertiesPages;
  root?: boolean;
  metadataId: string;
  currentOperationTreeItem: TreeViewDataItem;
  selectedAddOption: IContextualMenuItem;
  rootsWhereLinkedItems: TreeViewDataItem[];
}

export interface DocumentPropertiesProps {
  idPartRoot: string;
}

const addOptions: IContextualMenuItem[] = [
  {
    id: "add_before_menuitem",
    key: "before",
    text: formatMessage("cof.base.addbefore"),
  },
  {
    id: "add_after_menuitem",
    key: "after",
    text: formatMessage("cof.base.addafter"),
  },
];

const initialState: DocumentPropertiesState = {
  key: 1,
  partRoot: null,
  initialDocumentData: null,
  itemSelected: null,
  showMessagesModal: false,
  linkedPart: null,
  selectedPart: null,
  action: null,
  page: "outline",
  metadataId: null,
  currentOperationTreeItem: null,
  selectedAddOption: addOptions[0],
  rootsWhereLinkedItems: null,
  deletingPart: null,
};

export const DocumentProperties: React.FunctionComponent<DocumentPropertiesProps> = (props) => {
  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  const defaultServiceProvider: ServiceProvider = useAppSelector(selectServiceProvider);
  const seahorseDocumentLinked: SeahorseDocumentLinked[] = useAppSelector(selectSeahorseDocumentLinked);
  const allIxbrlSettings: IxbrlSetting[] = useAppSelector(selectIxbrlSettings);

  const { cloneDocPart } = useCloneDocPart(props.idPartRoot);
  let notAuthorizedUserForEdit =
    isNotAuthorizedUser(UserFunctionEnum.MENU_OUTLINE_PANEL_EDIT_DOCUMENT_PART, userFunctions) &&
    isNotAuthorizedUser(UserFunctionEnum.MENU_OUTLINE_PANEL_EDIT_DOCUMENT_ROOT, userFunctions);
  let notAuthorizedUserForDuplicate = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_DUPLICATE_DOCUMENTS,
    userFunctions
  );
  let notAuthorizedUserForNew = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_NEW_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForUnlink = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_UNLINK_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForDelete = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_DELETE_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForDetach = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_DETACH_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForSendCopyIXBRL = isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_USER, userFunctions);

  const addedElaborationsDetails = useAppSelector((store) => store.remoteElaborations.addedElaborationItemsList);
  const documentElabFindFunction = useCallback(
    (item: FindItem) =>
      item.elaborationType !== "SHEET_AND_AREA" && elaborationStatusItemIsBlocking(item, addedElaborationsDetails),
    [addedElaborationsDetails]
  );
  const blockingElaboration = useFilteredElaborations({
    findFunction: documentElabFindFunction,
  });

  useEffect(() => {
    if (!blockingElaboration) getInitialDocumentPartDetails();
  }, [blockingElaboration, props.idPartRoot]);

  const [state, setState] = useState<DocumentPropertiesState>(initialState);
  const [documentPartWithStatus, setDocumentPartWithStatus] = useState<DocumentPartWithStatus[]>([]);

  const getInitialDocumentPartDetails = async () => {
    const parents = await fetchRecursiveParentsDocumentPart(props.idPartRoot);
    const rootIdToUse = parents?.[0]?.idPadre ?? props.idPartRoot;

    const treeRootItem = await fetchDocumentPartDetails(rootIdToUse);

    const thisDocumentPart = await fetchDocumentPartDetails(props.idPartRoot);
    const rootsWhereLinked = thisDocumentPart.multiLinked ? await fetchRootsDocumentPart(props.idPartRoot) : [];
    const rootsWhereLinkedItems = await Promise.all(rootsWhereLinked.map((r) => mapDocPartToDataItem(r)));

    LoadingManager.remove(loadingManagerKey);
    setState((s) => ({
      ...s,
      itemSelected: null,
      partRoot: thisDocumentPart,
      initialDocumentData: {
        initialDocumentItem: {
          text: treeRootItem.name,
          key: treeRootItem.id,
          document: treeRootItem,
          readPermission: true,
        },
        parents,
      },
      rootsWhereLinkedItems: rootsWhereLinkedItems,
      metadataId: thisDocumentPart.metadata?.id,
      showMessagesModal: false,
    }));

    Office.context.document.getFilePropertiesAsync(function (asyncResult) {
      if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
        let sharePointFullPath = asyncResult.value.url.split("/");
        let sharepointFilename = sharePointFullPath[sharePointFullPath.length - 1];
        if (thisDocumentPart.name.replace(/\.docx$/, "") !== sharepointFilename.replace(/\.docx$/, ""))
          NotificationManager.addNotification({
            title: null,
            message: formatMessageWithParams("cof.outline.edit.documentnamenotmatch.info", {
              documentName: thisDocumentPart.name,
              sharepointName: sharepointFilename,
            }),
          });
      }
    });
  };

  function resetSelection() {
    LoadingManager.remove(loadingManagerKey);
    setState((s) => ({
      ...s,
      showMessagesModal: false,
      itemSelected: null,
      deletingPart: null,
      page: "outline",
    }));
    getInitialDocumentPartDetails();
  }

  function unlinkDocumentPartAndReset(selectedPart: DocumentPartItem) {
    unlinkDocumentPartAsync({
      linkedPartId: selectedPart.id,
      order: selectedPart.order,
      docPartId: state.partRoot.id,
    }).then(() => {
      resetSelection();
    });
  }

  async function checkAndUnlinkDocumentPart(selectedPart: DocumentPartItem) {
    const checked = await fetchCheckUnlinkPart(selectedPart.id);
    if (checked) {
      /* TODO check on BE if the unlink operation is checked against unlinking a part linked to only one father, thus remaining orphan
         skip this method and call unlinkDocumentPartAndReset(selectedPart) directly */
      console.error("One father, use detach or delete instead");
    } else {
      unlinkDocumentPartAndReset(selectedPart);
    }
  }

  function setPage(page: DocumentPropertiesPages) {
    setState((s) => ({ ...s, page: page }));
  }

  function haveSubmittedStatus(selectedItemKey: string, documentPartWithStatus: DocumentPartWithStatus[]) {
    return getWorkflowStatus(selectedItemKey, documentPartWithStatus)?.workflowStatusCode === SUBMITTED;
  }

  const getIconButtonMenuProps = (selectedItem: TreeViewDataItem, documentPartWithStatus: DocumentPartWithStatus[]) => {
    const isSonOfPartRoot = state.partRoot.id === selectedItem.data.fatherKey;
    const isDetachAvailable =
      isSonOfPartRoot &&
      !selectedItem.data.documentPart.root &&
      selectedItem.data.documentPart.multiLinked &&
      !selectedItem.data.fatherMultilinked;

    const isMultilinked =
      selectedItem.data.documentPart?.multiLinked && selectedItem.data.multilinkedParents?.length > 1;

    const isLinkedTwiceOrMore = state.partRoot.subparts.filter((part) => part.id === selectedItem.key).length > 1;

    const options: IContextualMenuItem[] = [
      {
        id: "unlink_menuitem",
        key: "unlink",
        text: formatMessage("cof.base.unlink"),
        onClick: () => {
          checkAndUnlinkDocumentPart(selectedItem.data.documentPart);
        },
        hidden: !(isSonOfPartRoot && !selectedItem.data.documentPart.root && (isMultilinked || isLinkedTwiceOrMore)),
        disabled:
          notAuthorizedUserForUnlink ||
          (documentPartWithStatus?.length > 0 && haveSubmittedStatus(selectedItem.key, documentPartWithStatus)),
      },
      {
        id: "delete_menuitem",
        key: "delete",
        text: formatMessage("cof.base.delete"),
        onClick: () => {
          setState((s) => ({ ...s, deletingPart: selectedItem.data.documentPart }));
        },
        hidden: !isSonOfPartRoot,
        disabled:
          notAuthorizedUserForDelete ||
          (documentPartWithStatus?.length > 0 && haveSubmittedStatus(selectedItem.key, documentPartWithStatus)),
      },
      {
        id: "detach_menuitem",
        key: "detach",
        text: formatMessage("cof.base.detach"),
        onClick: () => {
          setState((s) => ({
            ...s,
            page: "detach",
            currentOperationTreeItem: selectedItem,
          }));
        },
        hidden: !isDetachAvailable,
        disabled:
          notAuthorizedUserForDetach ||
          (documentPartWithStatus?.length > 0 && haveSubmittedStatus(selectedItem.key, documentPartWithStatus)),
      },
      {
        id: "export_menuitem",
        key: "export",
        text: formatMessage("cof.base.export"),
        onClick: () => {
          setState((s) => ({
            ...s,
            page: "export",
            currentOperationTreeItem: selectedItem,
          }));
        },
      },
      {
        id: "exportObjects_menuitem",
        key: "exportObjects",
        text: formatMessage("cof.base.exportobjects"),
        onClick: async () => {
          try {
            const file = await exportObjects(selectedItem.key);
            if (file) {
              const blob = new Blob([file], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
              saveAs(blob, "Intelligence Disclosure - Object export.xlsx");
            }
            NotificationManager.addNotification({
              key: INSERT_OBJECT_NOTIFICATION,
              message: formatMessage("cof.exportobjects.success"),
              title: null,
              type: "success",
              closable: true,
              removeOnClose: true,
              isTemporary: true,
            });
          } catch (error) {
            console.log(error);
          }
        },
      },
    ];

    if (!notAuthorizedUserForSendCopyIXBRL) {
      options.push({
        id: "sendcopyixbrl_menuitem",
        key: "sendcopyixbrl",
        text: formatMessage("cof.base.sendcopyixbrl"),
        onClick: () => {
          sendDocumentToIXBRL(selectedItem.data, defaultServiceProvider.id).then(() => {
            fetchSeahorseDocumentLinked().then((result) => store.dispatch(setSeahorseDocumentLinked(result)));
          });
        },
        hidden:
          selectedItem.key != state.initialDocumentData.initialDocumentItem.key ||
          hasNoIxbrlSetting(state.initialDocumentData.initialDocumentItem.key, allIxbrlSettings),
        disabled: hasNoDefaultServiceProvider(defaultServiceProvider),
      });
      options.push({
        key: "updated",
        text: formatSehorseLastUpdateDate(seahorseDocumentLinked, selectedItem.data.driveItemId),
        hidden: formatSehorseLastUpdateDate(seahorseDocumentLinked, selectedItem.data.driveItemId).length <= 0,
        disabled: true,
        className: "sentDateUpdate",
      });
    }
    return {
      items: options,
      directionalHintFixed: true,
    };
  };

  const onSaveDocument = async (input: DocumentData) => {
    LoadingManager.create(loadingManagerKey);
    const parents = await fetchParentsDocumentPart(input.documentId);
    const documentPart = await editDocumentMetadataAsync({
      name: input.name.replace(/\.docx$/, "") + ".docx",
      description: input.description,
      id: input.documentId,
      fatherPartId: input.fatherPartId,
      multiLinked: parents.length > 1 || input.multilinked || (parents.length == 1 && parents[0].multiLinked),
      metadata: mapAdvancedDocumentToDocPartMetadata({
        documentAdvancedCustomXML: {
          advanced: { ...input.advanced },
          language: input.language,
          documentId: input.documentId,
          parameters: input.parameters,
        },
      }),
    });
    console.log(state.itemSelected, props.idPartRoot);
    if (state.itemSelected.key != props.idPartRoot) {
      //IF the part we're editing is not the one for which the outline is opened
      const sharepointName = await fetchDriveItemName(
        state.itemSelected.data.data.driveItemId ?? state.itemSelected.data.data.documentPart.driveItemId
      ); //TODO
      if (sharepointName !== input.name) {
        NotificationManager.addNotification({
          title: null,
          message: formatMessageWithParams("cof.outline.edit.documentnamenotmatch.info", {
            documentName: input.name,
            sharepointName,
          }),
        });
      }
    }
    LoadingManager.remove(loadingManagerKey);
    editDocumentAdvancedCustomXML({
      ...input,
      advanced: { ...input.advanced, id: documentPart?.metadata?.id },
    });
    setState((s) => ({
      ...s,
      metadataId: documentPart?.metadata?.id,
      page: "outline",
    }));
    resetSelection();
  };

  const classNames = mergeStyleSets({
    pageContainer: {
      position: "relative",
      paddingBottom: "80px",
    },

    contentWrap: {
      overflow: "auto",
    },
    content: {
      position: "relative",
    },
  });

  const isRootSelected = useMemo(
    () => !state.itemSelected || state.itemSelected.key === state.partRoot.id,
    [state.itemSelected, state.partRoot]
  );

  const isMainDocument = useMemo(() => {
    return !state.itemSelected || state.itemSelected.key === state.initialDocumentData.initialDocumentItem.document.id;
  }, [state.initialDocumentData, state.itemSelected]);

  const isItemSelectedSonOfPartRoot = () => {
    // If the selected item is expanded, an extra "0" string is added to oidPath, so we can't go with the last index.
    // eg. ["rootId", "partId"] if not expanded, ["rootId", "partId", "0"] if expanded
    // We need to find the index of the selected item in the hierarchy
    const itemSelectedOidPathIndex = state.itemSelected.oidPath.findIndex(
      (parent) => parent === state.itemSelected.key
    );
    return state.itemSelected.oidPath[itemSelectedOidPathIndex - 1] === state.partRoot.id;
  };

  const addButtonProps: IButtonProps = {
    disabled:
      !state.itemSelected?.data.hasReadPermissions ||
      !(isItemSelectedSonOfPartRoot() || isRootSelected) ||
      notAuthorizedUserForNew ||
      (documentPartWithStatus?.length > 0 && haveSubmittedStatus(state.partRoot.id, documentPartWithStatus)),
    id: "add",
    onClick: () => setState((s) => ({ ...s, page: "adddocpart" })),
    text: formatMessage("cof.base.add").toUpperCase(),
    menuProps: isRootSelected
      ? null
      : {
          items: addOptions,
          isBeakVisible: false,
          onItemClick: (_, opt) => {
            checkIfPartExists(
              state.itemSelected?.item.data.documentPart?.id,
              state.itemSelected?.item.data.documentPart?.order,
              state.partRoot.id
            )
              .then(() => setState((s) => ({ ...s, page: "adddocpart", selectedAddOption: opt })))
              .catch((e) => {
                NotificationManager.addNotification({
                  title: e,
                  type: "error",
                  message: formatMessage("cof.objectsource.documentpart.selectednotexisting"),
                });
              });
          },
        },
  };

  const editButtonProps: IButtonProps = {
    id: "edit",
    disabled:
      !state.itemSelected?.data.hasReadPermissions ||
      notAuthorizedUserForEdit ||
      haveSubmittedStatus(state.partRoot.id, documentPartWithStatus),
    text: formatMessage("cof.base.edit"),
    onClick: () => setState((s) => ({ ...s, page: "edit" })),
  };

  const duplicateButtonProps: IButtonProps = {
    id: isMainDocument ? "duplicate" : "clone",
    disabled:
      !state.itemSelected?.data.hasReadPermissions ||
      notAuthorizedUserForDuplicate ||
      haveSubmittedStatus(state.partRoot.id, documentPartWithStatus) ||
      (!isRootSelected && !isItemSelectedSonOfPartRoot()) ||
      (isRootSelected && !isMainDocument),

    text: isMainDocument ? formatMessage("cof.base.rollforward") : formatMessage("cof.base.clone"),
    onClick: () => {
      if (!isMainDocument) {
        cloneDocPart(state.itemSelected);
        return;
      }

      setState((s) => ({
        ...s,
        page: "duplicate",
      }));
    },
  };

  function onItemClick(item: DocumentPartTreeSelection) {
    setState((s) => ({ ...s, itemSelected: item }));
  }

  //TODO DUPLICATION
  const [paths, setPaths] = useState<Map<string, string>>(new Map());
  const setPathForItem = useCallback((itemId: string, path: string) => setPaths((m) => m.set(itemId, path)), [paths]);
  const getPathForItem = useCallback((itemId: string) => paths.get(itemId), [paths]);

  if (state.page === "outline")
    return (
      <ElaborationWrapper
        elaborationItem={blockingElaboration}
        notificationKey={REFRESH_DOCUMENT_PROPERTIES_NOTIFICATION}
      >
        <>
          <HeaderPanel applyPadding>
            {formatMessageWithParams("cof.file.open.outline.title.documentoutline", {
              documentName: state.partRoot?.name?.replace(/\.docx$/, ""),
            })}
          </HeaderPanel>
          <div className="ms-Grid" dir="ltr">
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12" style={{ zIndex: "1" }}>
                {state.initialDocumentData && state.partRoot ? (
                  <div className={classNames.pageContainer}>
                    <div className={classNames.contentWrap}>
                      <div className={classNames.content}>
                        <DocumentOutline
                          onItemClick={onItemClick}
                          isOutlineRibbon={true}
                          selectedTreeItem={state.initialDocumentData.initialDocumentItem}
                          idPartRoot={props.idPartRoot}
                          getIconButtonOutlineRibbon={(item, _itemHierarchicalIndex, documentPartWithStatus) =>
                            getIconButtonMenuProps(item, documentPartWithStatus)
                          }
                          parents={state.initialDocumentData.parents}
                          documentPartWithStatus={documentPartWithStatus}
                          setDocumentPartWithStatus={setDocumentPartWithStatus}
                        />
                        {state.rootsWhereLinkedItems?.length ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: "1rem",
                            }}
                          >
                            {formatMessage("cof.outline.partcontainedin")}
                            {state.rootsWhereLinkedItems.map((r) => (
                              <TreeItemRow
                                item={r}
                                itemHierarchicalIndex=""
                                isSelected={false}
                                key={(r.data as DocumentPart).id + "ROOT"}
                                onClick={(event) => {
                                  if (event.detail === 2) {
                                    //double click
                                    openDocumentById(r.key);
                                  }
                                  event.preventDefault();
                                }}
                                pathInfo={{ setPathForItem, getPathForItem }}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  nullRender()
                )}
              </div>
            </div>
            <FooterPanel>
              <div
                className="ms-hiddenSm"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "5px",
                }}
              >
                <PrimaryButton {...editButtonProps} className={ButtonVariants.primary} />
                <PrimaryButton {...duplicateButtonProps} className={ButtonVariants.primary} />
                <PrimaryButton {...addButtonProps} className={ButtonVariants.primary} />
              </div>
              <div
                className="ms-hiddenMdUp"
                style={{
                  gap: ".25rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <IconButton
                  title={formatMessage("cof.base.edit")}
                  {...editButtonProps}
                  styles={{
                    icon: { height: "1.5rem" },
                    root: {
                      backgroundColor: editButtonProps.disabled ? "#D0D0D0" : null,
                      opacity: editButtonProps.disabled ? 0.5 : 1,
                    },
                  }}
                  iconProps={{
                    imageProps: {
                      src: "/assets/edit.svg",
                      imageFit: ImageFit.contain,
                      width: "1.5rem",
                      height: "1.5rem",
                    },
                  }}
                />
                <IconButton
                  title={duplicateButtonProps.text}
                  {...duplicateButtonProps}
                  styles={{
                    icon: { height: "1.5rem" },
                    root: {
                      backgroundColor: duplicateButtonProps.disabled ? "#D0D0D0" : null,
                      opacity: duplicateButtonProps.disabled ? 0.5 : 1,
                    },
                  }}
                  iconProps={{
                    imageProps: {
                      src: isRootSelected ? "/assets/roll-forward.svg" : "/assets/duplicate.svg",
                      imageFit: ImageFit.contain,
                      width: "1.5rem",
                      height: "1.5rem",
                    },
                  }}
                />
                <IconButton
                  title={formatMessage("cof.base.add")}
                  {...addButtonProps}
                  styles={{
                    icon: { height: "1.5rem" },
                    root: {
                      backgroundColor: addButtonProps.disabled ? "#D0D0D0" : null,
                      opacity: addButtonProps.disabled ? 0.5 : 1,
                    },
                  }}
                  iconProps={{
                    imageProps: {
                      src: "/assets/add.svg",
                      imageFit: ImageFit.contain,
                      width: "1.5rem",
                      height: "1.5rem",
                    },
                  }}
                />
              </div>
            </FooterPanel>
            <DeleteDocumentHelper
              partRoot={state.partRoot}
              currentDocumentPartId={props.idPartRoot}
              onCancelDelete={() => resetSelection()}
              deleteItem={state.deletingPart}
            />
          </div>
        </>
      </ElaborationWrapper>
    );
  if (state.page === "adddocpart")
    return (
      <CreateDocumentPartModal
        fatherPartId={state.partRoot.id}
        isFatherMultilinked={state.partRoot.multiLinked}
        referringPart={{
          id: state.itemSelected?.item.data.documentPart?.id ?? state.partRoot.id,
          order: state.itemSelected?.item.data.order,
        }} //TODO broken by the new pantarei ouline calls. To be resolved checking the mapper
        relativePosition={state.selectedAddOption.key as "before" | "after"}
        isModalOpen={true}
        onHide={() => resetSelection()}
        loadingManagerKey={loadingManagerKey}
      />
    );
  if (state.page === "edit")
    return (
      <>
        <HeaderPanel applyPadding>
          {state.itemSelected?.data.data.multiLinked ? (
            <>
              {formatMessage("cof.documentmanagement.outline.edit.editlinkedpart")}
              <span
                style={{
                  marginLeft: "0.5rem",
                  fontSize: "25px",
                }}
                className={"k-icon k-i-link-vertical k-rotate-45"}
              />
            </>
          ) : (
            formatMessage("cof.documentmanagement.outline.edit.editdocument")
          )}
        </HeaderPanel>
        <EditDocument
          onLastBackButton={() => setPage("outline")}
          isEdit={true}
          fatherPartId={state.partRoot.id !== state.itemSelected?.key ? state.partRoot.id : null}
          getDocumentPartDetails={async () => {
            const documentMetadata = await fetchDocumentMetadata(state.itemSelected?.key);
            const documentPart = await fetchDocumentPartDetails(state.itemSelected?.key);

            let inheritableParameters;

            if (!documentPart.root && !documentPart.multiLinked) {
              const parents = await fetchParentsDocumentPart(documentPart.id);
              if (parents.length === 1) {
                const fatherMetadata = await fetchDocumentMetadata(parents[0].id);
                inheritableParameters = fatherMetadata.parametersMetadata;
              }
            }

            const advanced = {
              numberFormat: documentMetadata.formatMetadata?.numberFormat?.format,
              typeNumber: documentMetadata.formatMetadata?.numberType,
              percentageFormat: documentMetadata.formatMetadata?.percentageFormat?.format,
              typePercentage: documentMetadata.formatMetadata?.percentageType,
              dateFormat: (documentMetadata.formatMetadata?.dateFormat as NumberFormat)?.format,
              typeDate: documentMetadata.formatMetadata?.dateType,
              language: documentMetadata.language,
              imageExtension: documentMetadata.formatImage?.imageExtension,
              imageResolution: documentMetadata.formatImage?.imageResolution,
              templateInfo: documentPart.metadata.templateInfo,
              locale: documentMetadata.formatMetadata?.locale,
              id: documentMetadata.id,
            };

            return {
              documentData: {
                description: documentPart.description,
                name: documentPart.name,
                root: documentPart.root,
                multilinked: documentPart.multiLinked,
                path: documentPart.path,
                documentId: documentPart.id,
                advanced,
                language: documentMetadata.language,
                parameters: documentMetadata.parametersMetadata,
              },
              fatherDocumentParameters: inheritableParameters,
            };
          }}
          onSave={onSaveDocument}
        />
      </>
    );
  if (state.page === "export")
    return (
      <ExportDocumentPanel
        documentId={state.currentOperationTreeItem.key}
        onCloseModal={() => resetSelection()}
        channelIdToNotify={props.idPartRoot}
      />
    );
  if (state.page === "detach")
    return (
      <DetachDocumentPanel
        documentId={state.currentOperationTreeItem.key}
        partOrder={state.currentOperationTreeItem.data.order}
        fatherDocumentId={props.idPartRoot}
        onCloseModal={() => resetSelection()}
        channelIdToNotify={props.idPartRoot}
      />
    );
  if (state.page === "duplicate")
    return (
      <DuplicateDocumentPanel
        documentId={state.itemSelected.key}
        documentName={state.itemSelected.name}
        onCloseModal={() => resetSelection()}
        channelIdToNotify={props.idPartRoot}
      />
    );
};
